import { formatShortDate } from "@clipboard-health/date-time";
import { PromotionBanner } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type PaymentsListItem } from "@src/appV2/redesign/Payments/Earnings/types/PaymentsListItem";
import { PAYMENT_DETAILS_FULL_PATH } from "@src/appV2/redesign/Payments/PaymentDetails/paths";
import { PAST_SHIFT_DETAILS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import {
  ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { isAfter, parseISO } from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { IconButton } from "../../../components/IconButton";
import { useGetWorkerPaymentsByShift } from "../../PastShiftDetails/api/useGetWorkerPaymentsByShift";

interface ExtraWorkedTimeRequestBannerProps {
  status: ExtraWorkedTimeRequestStatus.APPROVED | ExtraWorkedTimeRequestStatus.DECLINED;
  workplaceName: string;
  shiftDate: Date;
  shiftId: string;
  updatedAt: string;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  statusUpdatedBy?: ExtraWorkedTimeRequestStatusUpdater;
  onDismiss?: () => void;
}

function getBannerContent(params: {
  isApproved: boolean;
  isExpired: boolean;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  workplaceName: string;
  shiftDate: Date;
}): { title: string; subtitle: string } {
  const { isApproved, isExpired, isVerified, isFullShiftPayment, workplaceName, shiftDate } =
    params;

  const isFullShiftPaymentRequest = isFullShiftPayment ?? !isVerified;
  const formattedDate = formatShortDate(shiftDate);

  if (isApproved && isFullShiftPaymentRequest) {
    return {
      title: "Good news! Your full shift payment was approved.",
      subtitle: `${workplaceName} approved your full shift payment for your shift on ${formattedDate}.`,
    };
  }

  if (isApproved) {
    return {
      title: "Good news! Your pay adjustment was approved.",
      subtitle: `${workplaceName} approved your pay adjustment request for your shift on ${formattedDate}.`,
    };
  }

  if (isExpired && isFullShiftPaymentRequest) {
    return {
      title: "Your shift payment request expired.",
      subtitle: `Your shift payment request for your ${formattedDate} shift at ${workplaceName} wasn't reviewed and may have been missed.`,
    };
  }

  if (isExpired) {
    return {
      title: "Your pay adjustment request expired.",
      subtitle: `Your pay adjustment request for your ${formattedDate} shift at ${workplaceName} wasn't reviewed and may have been missed.`,
    };
  }

  if (isFullShiftPaymentRequest) {
    return {
      title: "Your full shift payment was declined.",
      subtitle: `${workplaceName} declined your full shift payment for your shift on ${formattedDate}.`,
    };
  }

  return {
    title: "Your pay adjustment was declined.",
    subtitle: `${workplaceName} declined your pay adjustment request for your shift on ${formattedDate}.`,
  };
}

// Temporary heuristic to find the payment associated with a missed punch request.
// This will be replaced once we can deterministically infer the payment ID directly from the missed punch request ID.
function findMissedPunchRequestPayment(
  payments: PaymentsListItem[],
  updatedAt: string
): PaymentsListItem | undefined {
  const updatedAtDate = parseISO(updatedAt);

  const eligiblePayments = payments.filter((payment) => {
    const { payoutStatus, description, createdAt } = payment.attributes;

    if (payoutStatus !== "paid") {
      return false;
    }

    if (!description.toLowerCase().includes("missed punch payment")) {
      return false;
    }

    return isAfter(parseISO(createdAt), updatedAtDate);
  });

  if (eligiblePayments.length === 0) {
    return undefined;
  }

  return eligiblePayments.reduce((closest, current) => {
    const closestDiff = parseISO(closest.attributes.createdAt).getTime() - updatedAtDate.getTime();
    const currentDiff = parseISO(current.attributes.createdAt).getTime() - updatedAtDate.getTime();
    return currentDiff < closestDiff ? current : closest;
  });
}

export function ExtraWorkedTimeRequestBanner(props: ExtraWorkedTimeRequestBannerProps) {
  const {
    status,
    workplaceName,
    shiftDate,
    shiftId,
    updatedAt,
    isVerified,
    isFullShiftPayment,
    statusUpdatedBy,
    onDismiss,
  } = props;

  const history = useHistory();
  const isApproved = status === ExtraWorkedTimeRequestStatus.APPROVED;
  const isExpired = !isApproved && statusUpdatedBy === ExtraWorkedTimeRequestStatusUpdater.SYSTEM;
  const isShiftHistoryPageRedesignEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_SHIFT_HISTORY_PAGE_REDESIGN,
    { defaultValue: false }
  );

  const { data: payments, isLoading: isLoadingPayments } = useGetWorkerPaymentsByShift(shiftId, {
    enabled: isApproved && isShiftHistoryPageRedesignEnabled,
  });

  const payment = isDefined(payments)
    ? findMissedPunchRequestPayment(payments, updatedAt)
    : undefined;
  const showApprovedCta =
    isShiftHistoryPageRedesignEnabled && isApproved && (isLoadingPayments || isDefined(payment));
  const showDeclinedCta = isShiftHistoryPageRedesignEnabled && !isApproved;
  const showCta = showApprovedCta || showDeclinedCta;

  const { title, subtitle } = getBannerContent({
    isApproved,
    isExpired,
    isVerified,
    isFullShiftPayment,
    workplaceName,
    shiftDate,
  });

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <PromotionBanner
        title={title}
        subtitle={subtitle}
        icon={
          isApproved ? "clock-success-with-dollar-colored" : "clock-error-with-exclamation-colored"
        }
        borderVariant="none"
        backgroundColor={(theme) =>
          isApproved ? theme.palette.payment!.background : theme.palette.intent!.error.gradient!
        }
        titleColor={(theme) =>
          isApproved ? theme.palette.payment!.text : theme.palette.intent!.error.text
        }
        {...(showApprovedCta && {
          ctaButtonText: "View payment details",
          ctaButtonVariant: "outlined" as const,
          ctaButtonIsLoading: isLoadingPayments,
        })}
        {...(showDeclinedCta && {
          ctaButtonText: "View details & resubmit",
          ctaButtonVariant: "outlined" as const,
        })}
        sx={(theme) => ({
          width: "100%",
          height: "100%",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.08)",
          // Add right padding to make room for the dismiss button
          ...(onDismiss && { paddingRight: theme.spacing(10) }),
          "& *": {
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
          "& .MuiTypography-body1": {
            fontSize: theme.typography.h5.fontSize,
            lineHeight: theme.typography.h5.lineHeight,
          },
          "& .MuiTypography-body2": {
            fontSize: theme.typography.body1.fontSize,
            lineHeight: theme.typography.body1.lineHeight,
          },
        })}
        {...(showCta && {
          onClick: () => {
            if (showApprovedCta && payment) {
              onDismiss?.();
              history.push(generatePath(PAYMENT_DETAILS_FULL_PATH, { paymentId: payment.id }));
            } else if (showDeclinedCta) {
              onDismiss?.();
              history.push(generatePath(PAST_SHIFT_DETAILS_FULL_PATH, { shiftId }));
            }
          },
        })}
      />
      {onDismiss && (
        <IconButton
          aria-label="Close"
          iconType="close"
          variant="ghost"
          size="small"
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(3),
            right: theme.spacing(3),
          })}
          onClick={(event) => {
            event.stopPropagation();
            onDismiss();
          }}
        />
      )}
    </Box>
  );
}
